import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProviderHeader from 'components/ProviderHeader';
import { useAuth } from 'context/AuthProvider';
import DevLoginModal from 'components/home/DevLoginModal';
import VariantCard, { VariantTheme } from 'components/home/VariantCard';
import api from 'lib/api';
import { navigateInApp } from 'lib/navigation';
import { API_ENDPOINTS } from 'constants/game';
import {
  applyVariantThemeCss,
  cacheVariantAssets,
  lobbyPathForVariant,
  rememberLastVariant,
  variantTableThemeColor,
} from 'lib/variant-assets';
import { variantTheme } from 'lib/format-variant';

interface GameVariant {
  id: number;
  code: string;
  name: string;
  description: string;
  status: boolean;
  card_back_style?: string | null;
  table_theme_color?: string | null;
  theme_audio_url?: string | null;
}

const HOME_VARIANTS: Array<{
  code: string;
  title: string;
  subtitle: string;
  description: string;
  theme: VariantTheme;
  cardLabels: string[];
  chipSymbol: 'spade' | 'crown';
}> = [
  {
    code: 'Classic',
    title: 'CLASSIC',
    subtitle: 'TEEN PATTI',
    description: 'Traditional Teen Patti - Classic Gameplay',
    theme: 'green',
    cardLabels: ['A♠', 'K♦', 'Q♥'],
    chipSymbol: 'spade',
  },
  {
    code: 'Muflis',
    title: 'MUFLIS',
    subtitle: 'TEEN PATTI',
    description: 'Lowest Hand Wins - Be the Last to Win',
    theme: 'purple',
    cardLabels: ['8♣', '7♠', '6♣'],
    chipSymbol: 'crown',
  },
  {
    code: 'AK47',
    title: 'AK47',
    subtitle: 'TEEN PATTI',
    description: 'A, K, 4, 7 are Jokers - High Action Gameplay',
    theme: 'red',
    cardLabels: ['A♥', 'K♥', '4♥', '7♥'],
    chipSymbol: 'spade',
  },
];

export default function Home() {
  const navigate = useNavigate();
  const { user, loading, devLogin, refreshSession } = useAuth();
  const [error, setError] = useState('');
  const [loginOpen, setLoginOpen] = useState(false);
  const [pendingRoute, setPendingRoute] = useState<string | null>(null);
  const [apiVariants, setApiVariants] = useState<GameVariant[]>([]);

  useEffect(() => {
    api.get<GameVariant[]>(API_ENDPOINTS.FRONTEND.VARIANTS).then((res) => {
      if (!res.success || !res.data) return;
      setApiVariants(res.data);
      cacheVariantAssets(res.data);
    });
  }, []);

  useEffect(() => {
    if (loading || user) return;
    void refreshSession();
  }, [loading, user, refreshSession]);

  const variantCards = useMemo(() => {
    return HOME_VARIANTS.map((preset) => {
      const fromApi = apiVariants.find(
        (v) => v.code.trim().toLowerCase() === preset.code.toLowerCase()
      );
      return {
        ...preset,
        description: fromApi?.description?.trim() || preset.description,
        isActive: fromApi ? fromApi.status === true : preset.code === 'Classic',
        tableThemeColor: variantTableThemeColor(fromApi?.code || preset.code),
        cardBackStyle: fromApi?.card_back_style || undefined,
      };
    });
  }, [apiVariants]);

  const requireAuthThenNavigate = (route: string) => {
    if (user) {
      navigateInApp(navigate, route);
      return;
    }
    setPendingRoute(route);
    setLoginOpen(true);
  };

  const handlePlay = (variantCode: string) => {
    const card = variantCards.find((v) => v.code.toLowerCase() === variantCode.toLowerCase());
    if (!card?.isActive) return;

    const route = lobbyPathForVariant(variantCode);
    rememberLastVariant(variantCode);
    applyVariantThemeCss(variantCode);

    if (user) {
      navigateInApp(navigate, route);
      return;
    }
    setPendingRoute(route);
    setLoginOpen(true);
  };

  const handleLogin = async (username: string) => {
    setError('');
    const ok = await devLogin(username);
    if (!ok) {
      setError('Login failed — check username exists in DB');
      return;
    }
    setLoginOpen(false);
    if (pendingRoute) {
      const route = pendingRoute;
      setPendingRoute(null);
      navigateInApp(navigate, route);
    }
  };

  return (
    <div className="provider-home relative flex min-h-screen flex-col overflow-hidden">
      <div className="provider-home-bg pointer-events-none absolute inset-0" aria-hidden />
      <div className="provider-home-vignette pointer-events-none absolute inset-0" aria-hidden />

      <ProviderHeader
        homeLinkTo="/home"
        onWalletClick={() => requireAuthThenNavigate('/transactions')}
      />

      <main className="relative z-10 flex flex-1 flex-col px-4 py-6 sm:px-8 lg:px-10">
        <div className="mx-auto flex w-full max-w-[1400px] flex-1 flex-col justify-center">
          <h2 className="provider-section-title text-center">Choose Your Variant</h2>

          {error && (
            <p className="mx-auto mt-4 max-w-lg rounded-lg border border-red-500/30 bg-red-950/40 px-4 py-2 text-center text-sm text-red-200">
              {error}
            </p>
          )}

          <div className="provider-home-variants mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
            {variantCards.map((variant) => (
              <VariantCard
                key={variant.code}
                title={variant.title}
                subtitle={variant.subtitle}
                description={variant.description}
                theme={variantTheme(variant.code)}
                cardLabels={variant.cardLabels}
                chipSymbol={variant.chipSymbol}
                tableThemeColor={variant.tableThemeColor}
                cardBackStyle={variant.cardBackStyle}
                active={variant.isActive}
                loading={false}
                onPlay={() => handlePlay(variant.code)}
              />
            ))}
          </div>

          <p className="mx-auto mt-10 max-w-xl text-center text-sm text-white/50">
            Private rooms live inside each variant lobby — pick a mode above, then create or join with a room code.
          </p>
        </div>
      </main>

      <DevLoginModal
        open={loginOpen}
        loading={loading}
        error={error}
        onClose={() => {
          setLoginOpen(false);
          setPendingRoute(null);
        }}
        onLogin={handleLogin}
      />
    </div>
  );
}
